import axios, { type AxiosInstance } from 'axios';
import { useAuthStore } from '../../../auth/useAuthStore';
import type {
  ImportCommitResponse,
  ImportFieldMapping,
  ImportPreviewResponse,
  ImportQualityResolutions,
  ImportRunDetailsResponse,
  ImportRunReportResponse,
  ImportSourceSnapshot,
  ImportValueMapping,
  SerializedImportableEntityDefinition,
} from './types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

function getAuthToken(): string | null {
  const storeToken = useAuthStore.getState().token;
  if (storeToken) {
    return storeToken;
  }

  try {
    const authStore = localStorage.getItem('auth-storage');
    if (!authStore) {
      return null;
    }

    const parsed = JSON.parse(authStore) as {
      state?: { token?: string };
      token?: string;
    };

    return parsed.state?.token || parsed.token || null;
  } catch (error) {
    console.warn('[WasteImportsApi] Error parsing auth token:', error);
    return null;
  }
}

function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('[WasteImportsApi] Unauthorized - missing, stale or expired token');
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

const api = createApiInstance();

interface PreviewPayload {
  projectId: string;
  moduleId: string;
  entityType: string;
  source: ImportSourceSnapshot;
  fieldMappings: ImportFieldMapping[];
  valueMappings: ImportValueMapping[];
  qualityResolutions?: ImportQualityResolutions;
}

interface CommitPayload extends PreviewPayload {
  previewFingerprint: string;
}

export const wasteImportsApi = {
  async getEntities(): Promise<SerializedImportableEntityDefinition[]> {
    const response = await api.get<SerializedImportableEntityDefinition[]>(
      '/imports/modules/waste/entities',
    );
    return response.data;
  },

  async preview(payload: PreviewPayload): Promise<ImportPreviewResponse> {
    const response = await api.post<ImportPreviewResponse>('/imports/preview', payload);
    return response.data;
  },

  async commit(payload: CommitPayload): Promise<ImportCommitResponse> {
    const response = await api.post<ImportCommitResponse>('/imports/commit', payload);
    return response.data;
  },

  async getRun(runId: string): Promise<ImportRunDetailsResponse> {
    const response = await api.get<ImportRunDetailsResponse>(`/imports/runs/${runId}`);
    return response.data;
  },

  async getRunReport(runId: string): Promise<ImportRunReportResponse> {
    const response = await api.get<ImportRunReportResponse>(
      `/imports/runs/${runId}/report`,
    );
    return response.data;
  },
};
